import { ArrowBack as ArrowBackIcon, CropFree as CropFreeIcon } from "@mui/icons-material";
import {
  AppBar,
  Autocomplete,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FilterOptionsState } from "@mui/material/useAutocomplete";
import { unwrapResult } from "@reduxjs/toolkit";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import PrimaryButton from "components/PrimaryButton";
import { PromptDialog } from "components/PromptDialog";
import StyledTextField from "components/StyledTextField";
import { BarcodeScannerDialog } from "components/barcodeScan";
import { scanditLicense } from "env";
import { addressToString, isAbortError } from "helpers";
import { filterMachineList } from "helpers/filterMachineList";
import { MachineType } from "operations/schema/schema";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  changeJobMachine,
  getMachines,
  selectIsJobInProgress,
  selectSelectedJob,
  setChangeJobMachineOpen,
} from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";

const StyledDetails = styled("div")(() => ({
  marginTop: 5,
  marginLeft: 14,
}));
interface ChangeJobMachineDialogProps {
  handleFinished: () => void;
}

export const ChangeJobMachineDialog: FC<ChangeJobMachineDialogProps> = (props) => {
  const { handleFinished } = props;

  const intl = useIntl();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const { machines, loading, loadingMachines, open } = useAppSelector(
    (state) => state.jobs.changeJobMachine
  );

  const [machineLoading, setMachineLoading] = useState(false);
  const [openScanner, setOpenScanner] = useState(false);
  const [machineACOpen, setMachineACOpen] = useState(false);
  const [openVisitInProgressDialog, setOpenVisitInProgressDialog] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<MachineType | null>(null);
  const [machineSearch, setMachineSearch] = useState<string>("");
  const [scannerValue, setScannerValue] = useState<string>("");
  const hasBarcodeScanner = !!scanditLicense();
  const currentMachine = job?.machine;
  const machineInstallDate = currentMachine?.installDate;
  const jobInProgress = useAppSelector(selectIsJobInProgress);

  const close = useCallback(() => {
    setSelectedMachine(null);
    setMachineSearch("");
    dispatch(setChangeJobMachineOpen({ open: false }));
  }, [dispatch]);

  const changeJobMachineCb = useCallback(
    (machineId: string) => {
      dispatch(changeJobMachine({ jobId: job.id, machineId }))
        .then(unwrapResult)
        .then(() => {
          setMachineLoading(false);
          handleFinished();
          close();
          dispatch(addSnackbarMessage({ key: "ChangeJobMachine-success" }));
        })
        .catch((error: any) => {
          if (isAbortError(error)) return;
          if (Array.isArray(error) && error.some((e) => e.message.includes("403 Forbidden"))) {
            dispatch(addSnackbarMessage({ key: "ChangeJobMachine-forbidden" }));
          } else {
            dispatch(addSnackbarMessage({ key: "ChangeJobMachine-fail" }));
          }
          setMachineLoading(false);
        });
    },
    [close, dispatch, handleFinished, job.id]
  );

  const onSave = useCallback(() => {
    setMachineLoading(true);
    changeJobMachineCb(selectedMachine?.id || "");
  }, [changeJobMachineCb, selectedMachine?.id]);

  const checkVisitInProgress = useCallback(() => {
    if (jobInProgress) {
      setOpenVisitInProgressDialog(true);
    } else {
      onSave();
    }
  }, [jobInProgress, onSave]);

  useEffect(() => {
    if (open) {
      if (job?.customer) {
        dispatch(
          getMachines({
            machineNo: "",
            customerId: job.customer.id,
          })
        );
      }
    }
  }, [open, job.customer, dispatch]);

  useEffect(() => {
    if (scannerValue) {
      const scanMatches = machines.filter((machine) => machine.machineNo === scannerValue);

      if (scanMatches.length === 1) {
        setSelectedMachine(scanMatches[0]);
      }
      setScannerValue("");
    }
  }, [scannerValue, machines]);

  return (
    <Dialog fullScreen open={open} onClose={close} data-testid="ChangeJobMachine-Dialog">
      <BackdropPrimaryMain open={machineLoading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={close}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="ChangeJobMachineDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" marginLeft="16px" flex={1}>
            {`${job?.externalId} `} / <FormattedMessage id="visit.changeMachine" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Card>
        {currentMachine && (
          <CardHeader
            data-testid="ChangeJobMachine-currentMachine"
            sx={{
              fontSize: "1.2rem",
            }}
            subheader={
              <>
                <Typography fontWeight={700}>{currentMachine.name}</Typography>
                <Typography>{currentMachine.machineNo}</Typography>
                {currentMachine.idNo && <Typography>{currentMachine.idNo}</Typography>}
                {currentMachine.location && <Typography>{currentMachine.location}</Typography>}
                {machineInstallDate && (
                  <Typography>
                    {`${intl.formatMessage({ id: "general.installed" })} `}
                    <FormattedDate
                      value={machineInstallDate}
                      day="numeric"
                      month="long"
                      year="numeric"
                    />
                  </Typography>
                )}
              </>
            }
          />
        )}
        {!currentMachine && (
          <CardHeader
            data-testid="ChangeJobMachine-currentMachine"
            sx={{
              fontSize: "1.2rem",
            }}
            subheader={
              <>
                <Typography fontWeight={700}>
                  <FormattedMessage id="changeJobEquipment.noMachine" />
                </Typography>
                <Typography></Typography>
              </>
            }
          />
        )}
        <CardContent sx={{ paddingTop: 0 }}>
          {!machineLoading && (
            <>
              <Grid container direction="column" spacing={2}>
                <Grid
                  item
                  container
                  alignItems="start"
                  justifyContent="space-between"
                  wrap="nowrap"
                >
                  <Grid item width="100%">
                    <Autocomplete
                      data-testid="ChangeJobMachine-autocomplete"
                      options={machines}
                      loading={loadingMachines}
                      open={machineACOpen}
                      onOpen={() => setMachineACOpen(true)}
                      onClose={() => setMachineACOpen(false)}
                      value={selectedMachine}
                      onChange={(_, value, reason) => {
                        if (reason === "clear") {
                          setSelectedMachine(null);
                          setMachineSearch("");
                        } else {
                          if (value) {
                            setSelectedMachine(value);
                          }
                        }
                      }}
                      getOptionLabel={(o: MachineType) => `${o.machineNo}`}
                      renderOption={(props, option) => {
                        return (
                          <li
                            {...props}
                            key={`${option.id}-${option.name}`}
                            style={{ display: "block" }}
                            data-testid={`${option.id}-${option.machineNo}`}
                          >
                            <Typography variant="body1">{option.machineNo}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {option.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {option.idNo}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {option.location}
                            </Typography>
                          </li>
                        );
                      }}
                      inputValue={machineSearch}
                      onInputChange={(_, value) => {
                        if (scannerValue && !value) {
                          setMachineSearch(scannerValue);
                        } else {
                          setScannerValue("");
                          setMachineSearch(value);
                        }
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionDisabled={(option) => option.id === currentMachine?.id}
                      filterOptions={(
                        options: MachineType[],
                        state: FilterOptionsState<MachineType>
                      ) => {
                        return filterMachineList(options, state.inputValue);
                      }}
                      renderInput={(params) => (
                        <StyledTextField
                          name="machine"
                          {...params}
                          label={intl.formatMessage({
                            id: "job.machine.machineNo",
                          })}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <Fragment>
                                {loadingMachines ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </Fragment>
                            ),
                          }}
                          required
                        />
                      )}
                    />
                    {!!selectedMachine?.id && (
                      <StyledDetails data-testid="ChangeJobMachine-SelectedMachine">
                        {!!selectedMachine.location && (
                          <Typography variant="body2" color="textSecondary">
                            {selectedMachine.location}
                          </Typography>
                        )}
                        {!!selectedMachine.name && (
                          <Typography variant="body2" color="textSecondary">
                            {selectedMachine.name}
                          </Typography>
                        )}
                        {!!selectedMachine.idNo && (
                          <Typography variant="body2" color="textSecondary">
                            {selectedMachine.idNo}
                          </Typography>
                        )}
                        {!!selectedMachine.address && (
                          <Typography variant="body2" color="textSecondary">
                            {addressToString(selectedMachine.address)}
                          </Typography>
                        )}
                      </StyledDetails>
                    )}
                  </Grid>
                  {hasBarcodeScanner && (
                    <Grid item>
                      <IconButton
                        data-testid="ChangeJobMachine-BarcodeButton"
                        onClick={() => setOpenScanner(true)}
                        size="large"
                        sx={{ pr: 0, ml: 1 }}
                      >
                        <CropFreeIcon fontSize="large" />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>

                <Grid item>
                  <PrimaryButton
                    key={`change-machine-submit-button`}
                    fullWidth
                    variant="contained"
                    disabled={!selectedMachine || loading || loadingMachines}
                    onClick={checkVisitInProgress}
                    data-testid="ChangeJobMachine-SaveButton"
                  >
                    <FormattedMessage id="general.save" />
                  </PrimaryButton>
                </Grid>
              </Grid>
              {hasBarcodeScanner && (
                <BarcodeScannerDialog
                  open={openScanner}
                  setOpen={(open: boolean) => setOpenScanner(open)}
                  submitValue={(value: string) => {
                    setScannerValue(value);
                    setMachineSearch(value);
                    setOpenScanner(false);
                    setMachineACOpen(true);
                  }}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
      <PromptDialog
        open={openVisitInProgressDialog}
        setOpen={setOpenVisitInProgressDialog}
        data-testid="ChangeJobMachine-VisitInProgressPrompt"
        onOk={() => {
          onSave();
        }}
        promptContent={<FormattedMessage id="changeJobEquipment.inProgressVisitReset" />}
      />
    </Dialog>
  );
};
